import { useEffect, useState } from "react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Switch } from "~/components/ui/switch";
import { Textarea } from "~/components/ui/textarea";
import { useCreateRawMaterial, useUpdateRawMaterial } from "~/hooks/purchasing";
import { useToast } from "~/hooks/use-toast";
import type { RawMaterial, RawMaterialFormData } from "~/types/purchasing";
import { MATERIAL_UNITS } from "~/types/purchasing";

interface RawMaterialDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  material?: RawMaterial | null;
}

export function RawMaterialDialog({
  open,
  onOpenChange,
  material,
}: RawMaterialDialogProps) {
  const { toast } = useToast();
  const createMutation = useCreateRawMaterial();
  const updateMutation = useUpdateRawMaterial();

  const isEditing = !!material;

  const [formData, setFormData] = useState<RawMaterialFormData>({
    name: "",
    code: "",
    description: "",
    unit: "UNI",
    minimum_stock_level: undefined,
    exclude_from_inventory: false,
    group_description: "",
    is_returnable_totem: false,
  });

  useEffect(() => {
    if (material) {
      setFormData({
        name: material.name,
        code: material.code,
        description: material.description || "",
        unit: material.unit,
        minimum_stock_level: material.minimum_stock_level ?? undefined,
        exclude_from_inventory: material.exclude_from_inventory ?? false,
        group_description: material.group_description || "",
        is_returnable_totem: material.is_returnable_totem ?? false,
      });
    } else {
      setFormData({
        name: "",
        code: "",
        description: "",
        unit: "UNI",
        minimum_stock_level: undefined,
        exclude_from_inventory: false,
        group_description: "",
        is_returnable_totem: false,
      });
    }
  }, [material, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.code.trim()) {
      toast({
        title: "Error",
        description: "Nombre y código son requeridos",
        variant: "destructive",
      });
      return;
    }

    if (isEditing && material) {
      updateMutation.mutate(
        { id: material.id, data: formData },
        {
          onSuccess: () => {
            toast({
              title: "Material actualizado",
              description: `${formData.name} ha sido actualizado`,
            });
            onOpenChange(false);
          },
          onError: (error) => {
            toast({
              title: "Error",
              description: error.message,
              variant: "destructive",
            });
          },
        }
      );
    } else {
      createMutation.mutate(formData, {
        onSuccess: () => {
          toast({
            title: "Material creado",
            description: `${formData.name} ha sido creado`,
          });
          onOpenChange(false);
        },
        onError: (error) => {
          toast({
            title: "Error",
            description: error.message,
            variant: "destructive",
          });
        },
      });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Editar Material" : "Nuevo Material"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Modifica los datos del material"
              : "Ingresa los datos del nuevo material"}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="code" className="text-right">
                Código *
              </Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    code: e.target.value.toUpperCase(),
                  })
                }
                className="col-span-3"
                placeholder="BOT-500, TAP-38..."
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                Nombre *
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="col-span-3"
                placeholder="Botella 500ml, Tapa 38mm..."
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="unit" className="text-right">
                Unidad *
              </Label>
              <Select
                value={formData.unit}
                onValueChange={(value) =>
                  setFormData({ ...formData, unit: value })
                }
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Seleccionar unidad" />
                </SelectTrigger>
                <SelectContent>
                  {MATERIAL_UNITS.map((unit) => (
                    <SelectItem key={unit.value} value={unit.value}>
                      {unit.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="minimum_stock" className="text-right">
                Stock Mín.
              </Label>
              <Input
                id="minimum_stock"
                type="number"
                min="0"
                value={formData.minimum_stock_level ?? ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    minimum_stock_level: e.target.value
                      ? parseFloat(e.target.value)
                      : undefined,
                  })
                }
                className="col-span-3"
                placeholder="Cantidad mínima"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="description" className="text-right">
                Descripción
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className="col-span-3"
                rows={2}
                placeholder="Descripción opcional..."
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="group_description" className="text-right">
                Grupo
              </Label>
              <Input
                id="group_description"
                value={formData.group_description ?? ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    group_description: e.target.value.toUpperCase(),
                  })
                }
                className="col-span-3"
                placeholder="ENVASES RETORNABLES, EMPAQUE..."
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="exclude_from_inventory" className="text-right">
                Sin Inventario
              </Label>
              <div className="col-span-3 flex items-center gap-2">
                <Switch
                  id="exclude_from_inventory"
                  checked={formData.exclude_from_inventory ?? false}
                  onCheckedChange={(checked) =>
                    setFormData({
                      ...formData,
                      exclude_from_inventory: checked,
                    })
                  }
                />
                <span className="text-sm text-muted-foreground">
                  No actualiza stock ni aparece en reportes
                </span>
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="is_returnable_totem" className="text-right">
                Tótem Retornable
              </Label>
              <div className="col-span-3 flex items-center gap-2">
                <Switch
                  id="is_returnable_totem"
                  checked={formData.is_returnable_totem ?? false}
                  onCheckedChange={(checked) =>
                    setFormData({
                      ...formData,
                      is_returnable_totem: checked,
                    })
                  }
                />
                <span className="text-sm text-muted-foreground">
                  El stock físico se gestiona en Gestión de Tótems
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? "Guardando..." : isEditing ? "Guardar" : "Crear"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
